'use client';

import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';
import { trpc } from '@/app/_trpc/client';
import { logger } from '@/lib/clientSideLogger';
interface LoginTrackerProps {
  userId: string;
  lastLoginDate: string | null;
}

export function LoginTracker({ userId, lastLoginDate }: LoginTrackerProps) {
  const { data: session, status } = useSession();

  const updateUser = trpc.users.update.useMutation({
    onError: (error) => {
      const enhancedError = new Error(error.message);
      enhancedError.cause = error;
      logger('error', enhancedError);
    },
  });

  const updateUserRef = useRef(updateUser.mutateAsync);
  updateUserRef.current = updateUser.mutateAsync;

  useEffect(() => {
    const checkAndUpdateLogin = async () => {
      const lastLogin = lastLoginDate ? new Date(lastLoginDate) : null;
      const shouldUpdate =
        !lastLogin || lastLogin < new Date(Date.now() - 1000 * 60 * 60 * 24);

      if (shouldUpdate) {
        const currentDate = new Date().toISOString();

        await updateUserRef.current({
          lastLoginDate: currentDate,
        });
      }
    };

    if (status !== 'loading' && !session?.user?.isLoggedInFromAdmin) {
      checkAndUpdateLogin().then(
        () => {
          // Set up interval to check every hour
          const interval = setInterval(checkAndUpdateLogin, 1000 * 60 * 60);
          return () => clearInterval(interval);
        },
        () => logger('error', new Error('Failed to check and update login'))
      );
    }
  }, [userId, lastLoginDate, status, session]);

  return null;
}
